import { useState, type ReactElement, type ReactNode } from 'react';
import { Stack } from '@mui/material';
import { rule } from '../lib/rules';
import { useDirectory } from '../lib/useDirectory';
import { DataTable, type Column } from './DataTable';
import { FilterBar, FilterSelect, PagedFooter, PageHeader } from './ListControls';
import { StatTile } from './StatTile';
import { TileGrid } from './TileGrid';
import type { Tone } from './tone';

type TileDef = { title: string; note: string; icon: ReactElement; tone: Tone };
type ExtraFilter<T> = { label: string; options: readonly string[]; matches: (item: T, value: string) => boolean };

type Props<T extends { status: string; addedDaysAgo: number }> = {
  title: string;
  subtitle: string;
  addLabel: string;
  /** "customers", "agents": used in "Showing 1 to 8 of 52 customers" */
  noun: string;
  items: T[];
  tiles: { total: TileDef; active: TileDef; fresh: TileDef; attention: TileDef };
  needsAttention: (item: T) => boolean;
  searchText: (item: T) => string;
  searchPlaceholder: string;
  /** dropdowns beside the search box (a Status dropdown is always added) */
  filters?: ExtraFilter<T>[];
  columns: Column<T>[];
  rowKey: (item: T) => string;
  onOpen: (item: T) => void;
  addDialog: (dialog: { open: boolean; onClose: () => void }) => ReactNode;
  size?: number;
};

/**
 * The list page for a kind of company (customers, agents, carriers): four tiles that filter, a search box and
 * dropdowns, the table, the page footer and an "add" dialog. Each page only says what is different.
 */
export function DirectoryPage<T extends { status: string; addedDaysAgo: number }>({
  title,
  subtitle,
  addLabel,
  noun,
  items,
  tiles,
  needsAttention,
  searchText,
  searchPlaceholder,
  filters = [],
  columns,
  rowKey,
  onOpen,
  addDialog,
  size = 8,
}: Props<T>) {
  const dir = useDirectory(items, size, needsAttention, searchText);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const shown = dir.base.filter((i) =>
    filters.every((f) => !chosen[f.label] || chosen[f.label] === 'all' || f.matches(i, chosen[f.label]!)),
  );
  const rows = dir.paging.slice(shown);
  const tile = (key: keyof typeof tiles, view: 'all' | 'active' | 'new' | 'attention', count: number) => (
    <StatTile count={count} {...tiles[key]} onClick={() => dir.setView(view)} selected={dir.view === view} />
  );

  return (
    <Stack spacing={2}>
      <PageHeader title={title} subtitle={subtitle} newLabel={addLabel} onNew={() => dir.setAdding(true)} noExport />
      <TileGrid>
        {tile('total', 'all', items.length)}
        {tile('active', 'active', items.filter((i) => i.status === 'Active').length)}
        {tile('fresh', 'new', items.filter((i) => i.addedDaysAgo <= rule('newWithinDays')).length)}
        {tile('attention', 'attention', items.filter(needsAttention).length)}
      </TileGrid>
      <FilterBar placeholder={searchPlaceholder} search={dir.search} onSearch={dir.setSearch}>
        {filters.map((f) => (
          <FilterSelect
            key={f.label}
            label={f.label}
            value={chosen[f.label] ?? 'all'}
            onChange={dir.paging.reset((v: string) => setChosen((c) => ({ ...c, [f.label]: v })))}
            options={f.options}
          />
        ))}
        <FilterSelect label="Status" value={dir.status} onChange={dir.setStatus} options={['Active', 'Inactive']} />
      </FilterBar>
      <DataTable<T>
        rows={rows}
        rowKey={rowKey}
        empty={items.length ? `No ${noun} match.` : `No ${noun} yet.`}
        onRowClick={onOpen}
        columns={columns}
      />
      <PagedFooter noun={noun} total={shown.length} page={dir.paging.page} size={size} onPage={dir.paging.setPage} />
      {addDialog({ open: dir.adding, onClose: () => dir.setAdding(false) })}
    </Stack>
  );
}
